/**
 * The audit timeline (INV-02 "Timeline"), read from `GET
 * /v1/invoices/{id}/journal` — the read-only sub-ledger that is the auditor's
 * trail from the invoice to each posting (LLD IVC-150/IVC-151).
 *
 * Two joins are deliberate and both are stated on screen:
 *
 *   - A journal entry names a `paymentId`, and the transaction hash lives on that
 *     payment. The timeline shows the hash by joining on that id, and says so.
 *   - `JournalEntry` has **no actor field**. Rather than guessing which entry
 *     kind corresponds to an issue or a void (the kind is a free-form string in
 *     this contract), the document's own `issuedBy` / `voidedBy` fields are
 *     shown above the entries and `journalActorNote` explains the gap. An
 *     invented per-entry actor is exactly the kind of audit fiction this page
 *     must not produce.
 *
 * Debit and credit are rendered exactly as the service stores them — base-unit
 * strings, monospaced. The entry does not declare the asset's decimals, so
 * formatting them into token units would mean inventing a decimals value; the
 * note under the list says they are base units instead.
 */

import type { ReactNode } from "react";
import { EmptyState } from "@/components/ui/feedback";
import type { Invoice, JournalEntry, Payment } from "@/features/invoicing/types";
import { DETAIL_COPY } from "./copy";

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
      <span className="text-muted">{label}</span>
      <span className="min-w-0 break-all text-ink">{value}</span>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string | null | undefined }) {
  return <Row label={label} value={<code dir="ltr">{value ?? DETAIL_COPY.emptyValue}</code>} />;
}

function JournalEntryCard({ entry, payment }: { entry: JournalEntry; payment: Payment | undefined }) {
  return (
    <li
      data-testid={`journal-entry-${entry.entryId}`}
      className="rounded-md border border-line bg-surface px-3.5 py-3"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <code dir="ltr" className="font-mono text-[13px] font-semibold text-ink">
          {entry.kind}
        </code>
        <code dir="ltr" data-testid={`journal-entry-created-${entry.entryId}`} className="font-mono text-[11px] text-muted">
          {entry.createdAt}
        </code>
        {entry.pendingPrice && (
          <span className="rounded-full border border-warning-border bg-warning-soft px-2 py-0.5 text-[11px] text-warning">
            {DETAIL_COPY.journalPendingPrice}
          </span>
        )}
        {entry.depeg && (
          <span className="rounded-full border border-info-border bg-info-soft px-2 py-0.5 text-[11px] text-info">
            {DETAIL_COPY.journalDepeg}
          </span>
        )}
      </div>

      <div className="mt-2 grid gap-1">
        <Line label="entry" value={entry.entryId} />
        {entry.paymentId && (
          <Row
            label="payment"
            value={
              <span className="inline-flex flex-wrap items-baseline gap-x-2">
                <code dir="ltr">{entry.paymentId}</code>
                {payment && (
                  <code dir="ltr" data-testid={`journal-entry-tx-${entry.entryId}`} className="font-mono text-[11px] text-muted">
                    {payment.network} {payment.txHash}
                  </code>
                )}
              </span>
            }
          />
        )}
        {entry.resolutionId && <Line label="resolution" value={entry.resolutionId} />}
        {entry.priceSnapshotRef && <Line label="price snapshot" value={entry.priceSnapshotRef} />}
        <Line label="idempotency key" value={entry.idempotencyKey} />
        {entry.reversesEntryId && (
          <Row label="reverses" value={<code dir="ltr">{entry.reversesEntryId}</code>} />
        )}
      </div>

      <div className="mt-2 overflow-hidden rounded-sm border border-line">
        <table className="data-table dense">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Account</th>
              <th scope="col">Asset</th>
              <th scope="col">Debit</th>
              <th scope="col">Credit</th>
            </tr>
          </thead>
          <tbody>
            {entry.lines.map((line) => (
              <tr key={`${entry.entryId}-${line.lineNo}`}>
                <td className="font-mono text-[12px] text-muted">{line.lineNo}</td>
                <td className="font-mono text-[12px] text-ink">{line.account}</td>
                <td className="text-ink">{line.assetSymbol}</td>
                <td className="font-mono text-[12px] text-ink">{line.debit}</td>
                <td className="font-mono text-[12px] text-ink">{line.credit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </li>
  );
}

export function JournalTimeline({
  entries,
  payments,
  invoice,
}: {
  entries: JournalEntry[];
  payments: Payment[];
  invoice: Invoice;
}) {
  const byPaymentId = new Map(payments.map((p) => [p.paymentId, p]));

  return (
    <section aria-labelledby="invoice-journal-heading" data-testid="invoice-journal">
      <h2 id="invoice-journal-heading" className="mb-2 text-sm font-bold text-ink">
        {DETAIL_COPY.journalHeading}
      </h2>

      <div className="mb-3 grid gap-1 rounded-md border border-line bg-bg px-3.5 py-3">
        {invoice.issuedBy && (
          <Row
            label={DETAIL_COPY.issuedByLabel}
            value={
              <span className="inline-flex flex-wrap items-baseline gap-x-2">
                <code dir="ltr" data-testid="journal-actor-issued-by">{invoice.issuedBy}</code>
                {invoice.issuedAt && <code dir="ltr" className="font-mono text-[11px] text-muted">{invoice.issuedAt}</code>}
              </span>
            }
          />
        )}
        {invoice.voidedBy && (
          <Row
            label={DETAIL_COPY.voidedByLabel}
            value={<code dir="ltr" data-testid="journal-actor-voided-by">{invoice.voidedBy}</code>}
          />
        )}
        {invoice.voidReason && <Line label={DETAIL_COPY.voidReasonLabel} value={invoice.voidReason} />}
        <p className="mt-1 max-w-[680px] text-xs leading-relaxed text-muted">
          {DETAIL_COPY.journalActorNote} {DETAIL_COPY.journalTxHashNote}
        </p>
      </div>

      {entries.length === 0 ? (
        <EmptyState
          card
          title={DETAIL_COPY.journalEmptyTitle}
          description={DETAIL_COPY.journalEmptyDescription}
        />
      ) : (
        <>
          <ol className="flex flex-col gap-3">
            {entries.map((entry) => (
              <JournalEntryCard key={entry.entryId} entry={entry} payment={entry.paymentId ? byPaymentId.get(entry.paymentId) : undefined} />
            ))}
          </ol>
          <p className="mt-2 text-xs leading-relaxed text-muted">{DETAIL_COPY.journalAmountsInBaseUnits}</p>
        </>
      )}
    </section>
  );
}
